import { SentenceOrder } from './order.ts';
import { audioSession } from './session.ts';
import { readVolume, volumeToGain } from './volume.ts';
import type { ServerFrame } from './transport.ts';

/**
 * Plays synthesised sentences back to back without gaps.
 *
 * Sentences can finish synthesising out of order, so each is buffered under its
 * index and released only when its turn comes.
 */
/**
 * The output chain's three constants, in dB, measured rather than chosen.
 *
 * A Kokoro-82M-bf16 sample of ordinary tutor speech (`af_heart`, speed 1.0)
 * peaks at -14.4 dBFS with a speech-only RMS of -28.2 dBFS. Nothing downstream
 * made that up -- this queue used to connect each source straight to
 * `destination`.
 *
 * **The reference is media loudness, not broadcast speech.** The first attempt
 * at this used +10 dB, which lands the RMS at -18.2 dBFS -- correct for the
 * -18 to -20 dBFS a speech mix is mastered to, and still 4.2 dB below the
 * -14 LUFS ordinary media sits at. A learner on a phone at full volume
 * compares the tutor against every other app on the device, not against a
 * mixing desk, and reported it still too quiet to hear. These numbers target
 * the loudness of a phone call instead, which is what a voice-only app on a
 * phone speaker is actually competing with.
 *
 * Simulated over the sample above before shipping: peak -5.0 dBFS, speech RMS
 * -12.6 dBFS, and not one clipped sample.
 *
 * **-12.6, not -9.6.** The first pass at these numbers targeted the loudness
 * of a phone call, which was reported back as too loud. -12.6 dBFS sits 1.4 dB
 * above the -14 LUFS ordinary media sits at: still clearly audible on a phone
 * speaker, which is the whole point, without being the loudest thing on the
 * device. Only `POST_GAIN_DB` moved -- the compression is what makes the quiet
 * syllables audible and it should not be traded away to lower the peaks.
 *
 * Re-measure all three before changing any of them. A different TTS model or
 * voice has its own level, and these are only correct for the one they were
 * taken from.
 */
const PRE_GAIN_DB = 18;
const LIMIT_THRESHOLD_DB = -9;
const LIMIT_RATIO = 8;
const POST_GAIN_DB = 1;

const dbToGain = (db: number): number => 10 ** (db / 20);

export class PlaybackQueue {
  private ctx = new AudioContext();
  private order = new SentenceOrder<AudioBuffer>();
  private playAt = 0;
  /**
   * The sources scheduled and not yet finished, so [`flush`] can stop them.
   *
   * There is no other handle on them: `play` schedules each one ahead of the
   * last on `playAt` and then forgets it, which is what makes the reply
   * gapless and also what leaves seconds of already-scheduled audio with
   * nothing able to reach it.
   */
  private scheduled = new Set<AudioBufferSourceNode>();
  /**
   * Set by [`flush`], cleared by the terminator.
   *
   * Cancellation on the server is cooperative -- the turn checks the signal at
   * its own boundaries -- so chunks of the cancelled reply are still in flight
   * when the flush runs, and a sentence that arrives after it would schedule
   * itself onto an empty timeline and speak. Everything up to `TTS_DONE`
   * belongs to the reply that was cancelled; the terminator is what says the
   * next chunk belongs to a reply the learner still wants.
   */
  private discarding = false;
  private tail: Promise<void> = Promise.resolve();
  /**
   * Frames handed to `onFrame` whose step on `tail` has not finished — a
   * sentence still decoding is audio on its way, which `isSilent` counts.
   */
  private pending = 0;
  /** The reply now playing, buffer by buffer in play order (hear-again design §4.1). */
  private heard: AudioBuffer[] = [];
  /**
   * The last reply that played to its end: what a tap replays. Promoted from
   * `heard` only in `settleSilence`, which a barge-in's `flush` disarms, so a
   * reply the learner cut off never becomes it.
   */
  private lastReply: AudioBuffer[] = [];
  /**
   * A replay's sources, apart from `scheduled`: nothing that reads the turn --
   * `lastPlayed`, `isSilent`, the `CANCEL`'s index -- may see a replay (§2).
   */
  private replaying = new Set<AudioBufferSourceNode>();
  /**
   * `source -> gain -> limiter -> destination`, built once.
   *
   * Built once rather than per sentence because a per-sentence gain staged
   * from that sentence's own peak would pump: a quiet sentence would be
   * lifted further than a loud one and the level would jump mid-reply, which
   * is more distracting than being uniformly quiet.
   *
   * The limiter is a safety net, not an effect. `MAKEUP_GAIN` is set from one
   * measured sample and Kokoro's level varies by utterance, so a louder-than-
   * measured one would clip on its own; a high ratio just below full scale
   * catches it instead. It is inaudible on anything at the measured level.
   */
  private gain = this.buildOutputChain();
  /**
   * The last sentence whose source reached its end — spec §7's payload. `null`
   * until one does, and reset per turn because the server numbers sentences
   * from zero for every utterance.
   */
  private lastPlayed: number | null = null;
  /**
   * Arms the same way `SentenceOrder.endTurn` arms `newTurn` — see that
   * class's comment for why. `TTS_DONE` means no more audio is coming, not
   * that `playAt`'s already-scheduled audio has finished playing, so
   * clearing `lastPlayed` on arrival would report "nothing heard" for a turn
   * still audibly finishing.
   */
  private turnEnded = false;
  /**
   * Armed by the terminator, disarmed when the last scheduled source ends.
   *
   * The same arm-then-act shape as `turnEnded` above, for the same reason and
   * with a different subject: that one is about the index the `CANCEL`
   * carries, this one about whether the tutor can still be heard.
   */
  private awaitingSilence = false;
  /**
   * Called when a finished turn's audio actually stops -- see [`armSilence`].
   *
   * A plain field assigned in the constructor, not a parameter property:
   * `node --test` strips types without transforming them and refuses that
   * syntax outright.
   */
  private readonly onSilent: () => void;
  private readonly onReplayEnded: () => void;

  constructor(onSilent: () => void, onReplayEnded: () => void = () => {}) {
    this.onSilent = onSilent;
    this.onReplayEnded = onReplayEnded;
  }

  private buildOutputChain(): GainNode {
    // `playback`, so the mute switch cannot silence the tutor. Claimed here,
    // where audio is about to play; the microphone claims its own category
    // for as long as it is open (media-channel design §4.1).
    audioSession.playback();

    const pre = this.ctx.createGain();
    pre.gain.value = dbToGain(PRE_GAIN_DB);

    // Compression, not brick-wall limiting. A ratio of 8 above -9 dBFS pulls
    // the loud syllables down far enough that `post` can lift the whole line
    // without the peaks clipping -- which is what raises *perceived* loudness.
    // Gain alone cannot: it moves peak and RMS together, so it runs out of
    // headroom at the peaks while the quiet syllables are still inaudible.
    //
    // Hard knee, because the numbers above were simulated with one. A soft
    // knee would sound slightly smoother and would make that simulation a
    // description of something else.
    const limiter = this.ctx.createDynamicsCompressor();
    limiter.threshold.value = LIMIT_THRESHOLD_DB;
    limiter.knee.value = 0;
    limiter.ratio.value = LIMIT_RATIO;
    // Fast enough to catch a plosive, slow enough not to chew the vowel after
    // it: release well under a syllable would pump audibly on speech.
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;

    // The learner's trim rides on the post gain, which sits *after* the
    // compressor -- so raising it raises the output directly and can clip.
    // `volumeToGain` is capped for exactly that reason; see its comment for
    // the headroom arithmetic.
    //
    // Read once, here, rather than watched: this chain is built when a session
    // starts, and the settings screen that changes it is somewhere a learner
    // can only be when no session is running. A live subscription would be
    // machinery for a transition that cannot happen.
    const post = this.ctx.createGain();
    post.gain.value = dbToGain(POST_GAIN_DB) * volumeToGain(readVolume());

    pre.connect(limiter);
    limiter.connect(post);
    post.connect(this.ctx.destination);
    return pre;
  }

  /**
   * `??`, not a bare field read: `Object.create(PlaybackQueue.prototype)` in
   * `playback.test.ts` bypasses this field's initialiser, leaving it
   * `undefined` rather than `null`.
   */
  lastPlayedSentence(): number | null {
    return this.lastPlayed ?? null;
  }

  /** Visible for the tests, and for `play`'s `onended`. */
  noteEnded(sentence: number): void {
    this.lastPlayed = sentence;
  }

  /** Visible for the tests, and for `onFrame`'s `"done"` branch. */
  armTurnReset(): void {
    this.turnEnded = true;
  }

  /**
   * The terminator arrived: report silence once the audio behind it has run
   * out.
   *
   * `TTS_DONE` is not the end of the tutor's voice, only of its supply.
   * `play` schedules sentences back to back on `playAt` and synthesis runs far
   * ahead of real time -- `docs/BUILD.md` records a first sentence synthesised
   * in 254-822 ms and spoken over 2-4 s -- so the terminator lands seconds
   * before the reply is audibly over. Anything hung on "is the tutor
   * speaking?" has to hang on this rather than on the frame: spec §7's
   * sustained-speech guard is off, and barge-in itself inoperative, for
   * whatever window the two disagree by.
   *
   * Settled immediately as well as armed, because a turn that synthesised
   * nothing -- a failure, an over-cap refusal -- has no source whose end could
   * carry it.
   */
  armSilence(): void {
    this.awaitingSilence = true;
    this.settleSilence();
  }

  /**
   * Nothing playing, nothing scheduled and nothing decoding: what a session
   * the server ended waits for before it shows its ending (`ServerEnd`).
   * `?? 0` because `Object.create` in the tests bypasses the initialisers.
   */
  isSilent(): boolean {
    return (this.pending ?? 0) === 0 && !this.awaitingSilence && (this.scheduled?.size ?? 0) === 0;
  }

  hasReply(): boolean {
    return this.lastReply.length > 0;
  }

  /**
   * Play the last whole reply again, from the start (hear-again design §4.1).
   * Refused while anything of a turn is decoding or audible, or while a replay
   * already is, so a tap can never overlap the tutor or stutter a replay.
   */
  replay(): boolean {
    if (this.lastReply.length === 0 || this.replaying.size > 0 || !this.isSilent()) return false;
    // A tap is a user gesture, so this is where a context the autoplay policy
    // suspended can be resumed.
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch((e: unknown) => console.warn('playback: could not resume for a replay', e));
    }
    let at = this.ctx.currentTime;
    for (const buffer of this.lastReply) {
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(this.gain);
      source.onended = () => {
        this.replaying.delete(source);
        if (this.replaying.size === 0) this.onReplayEnded();
      };
      source.start(at);
      this.replaying.add(source);
      at += buffer.duration;
    }
    return true;
  }

  /** A capture, a new reply or the session's end took over (§3). */
  stopReplay(): void {
    if (this.replaying.size === 0) return;
    for (const source of this.replaying) {
      source.onended = null;
      source.stop();
    }
    this.replaying.clear();
    this.onReplayEnded();
  }

  private settleSilence(): void {
    if (!this.awaitingSilence || this.scheduled.size > 0) return;
    this.awaitingSilence = false;
    // A turn that synthesised nothing leaves the last whole reply standing.
    if (this.heard.length > 0) this.lastReply = this.heard;
    this.heard = [];
    this.onSilent();
  }

  /**
   * Barge-in -- spec §7. Silence the reply now, rather than letting what is
   * already scheduled play out.
   *
   * Not `stop()` below, which suspends the context: sources survive a suspend
   * and `playAt` is in the suspended clock, so resuming would play the
   * cancelled reply and schedule the next one behind all of it. That turns a
   * truncation into a delay.
   *
   * Two things depend on this being immediate rather than tidy. The index the
   * `CANCEL` carried is the last sentence the learner *heard*, and it is only
   * true if nothing after it sounds. And `BargeIn` clears the worklet's
   * `tutorSpeaking` on the same tick, which is only correct if the tutor has
   * actually stopped -- otherwise the sustained-speech guard is off for
   * exactly the seconds the tutor is still audible.
   */
  flush(): void {
    for (const source of this.scheduled) {
      // Before `stop`, which fires `onended` of its own. A sentence cut off
      // mid-word was not played to completion, and letting it run
      // `noteEnded` would advance the last-played index past what the
      // `CANCEL` just reported.
      source.onended = null;
      source.stop();
    }
    this.scheduled.clear();
    this.heard = [];
    this.playAt = 0;
    // The indices it is holding belong to a reply that is over. Resetting it
    // here rather than leaning on the cancelled turn's own `TTS_DONE` to arm
    // the reset: that terminator does arrive, but a flush that only works
    // because of a later frame is a flush with a window in it.
    this.order = new SentenceOrder<AudioBuffer>();
    // Only a reply that is still *arriving* needs discarding, and
    // `awaitingSilence` says exactly that this turn's terminator has already
    // landed. Most barge-ins are that case, because generation finishes
    // seconds before the audio does -- and nothing but a terminator clears
    // `discarding`, so setting it unconditionally here would swallow the next
    // reply whole: the one answering the learner who just cut in.
    this.discarding = !this.awaitingSilence;
    // Disarmed rather than settled: the caller has silenced the tutor itself
    // and dropped the guard on this tick (see `BargeIn.captureStarted`). An
    // arm left standing would fire in the next reply's first gap between
    // scheduled sentences, switching the guard off mid-speech.
    this.awaitingSilence = false;
  }

  /** Visible for the tests, and for `enqueue`. */
  noteSentenceArrived(): void {
    if (this.turnEnded) {
      this.turnEnded = false;
      this.lastPlayed = null;
    }
  }

  /**
   * Feed the socket straight in.
   *
   * The turn boundary lives here rather than in the view because the view
   * would have to remember it on every turn, and forgetting means the client
   * goes silent after the first reply with nothing to show for it.
   *
   * Frames are handled one at a time, in arrival order, and that is what makes
   * the boundary correct. `enqueue` decodes asynchronously, so handling frames
   * as they arrive let `done` reach `SentenceOrder` while earlier sentences
   * were still decoding: the turn reset before its own last sentence had been
   * accepted, which stranded that sentence and then released it in the middle
   * of the next turn, out of order and in place of the real one. The socket
   * delivers frames in order; the queue is what keeps `SentenceOrder` seeing
   * them in that order too.
   */
  onFrame(frame: ServerFrame): void {
    // A new reply always wins over a replay, and on the frame rather than after
    // its decode, so the two never overlap (§3).
    if (frame.kind === 'chunk') this.stopReplay();
    this.pending += 1;
    this.tail = this.tail
      .then(async () => {
        if (frame.kind === 'chunk') {
          if (this.discarding) return;
          await this.enqueue(frame.sentence, frame.audio);
        } else if (frame.kind === 'done') {
          this.discarding = false;
          this.order.endTurn();
          this.armTurnReset();
          this.armSilence();
        }
      })
      // Keeps the chain running. A rejection left unhandled would settle `tail`
      // rejected for good, and every later frame in the session would be
      // dropped with it. A sentence that will not decode is handled where it
      // fails, in `enqueue`, because dropping it here is not enough.
      .catch((e: unknown) => console.warn('playback: dropped a frame', e))
      .finally(() => {
        this.pending -= 1;
      });
  }

  async enqueue(sentence: number, wav: ArrayBuffer): Promise<void> {
    this.noteSentenceArrived();

    // The context is built in `startSession`'s `onCompleted` — a mutation
    // callback, not the click handler — so it is not created inside a user
    // gesture and autoplay policy can leave it suspended. Resuming a running
    // context is a no-op; not resuming means the first reply is never heard.
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    let buffer: AudioBuffer | null = null;
    try {
      buffer = await this.ctx.decodeAudioData(wav);
    } catch (e) {
      console.warn(`playback: sentence ${sentence} would not decode`, e);
    }

    // After the decode, not only in `onFrame`: that guard is upstream of this
    // await, so a sentence already decoding when the flush ran has passed it.
    // Both branches below touch `this.order`, and the flush replaced it -- an
    // index released against the new one belongs to the next reply and would
    // strand its real sentence behind a ghost.
    if (this.discarding) return;

    // A failed decode is released, not dropped. `SentenceOrder` plays sentence
    // n only once n - 1 has played, so a sentence that never arrives is not
    // one silent sentence — it strands every later sentence in the turn.
    const ready = buffer === null ? this.order.skip(sentence) : this.order.accept(sentence, buffer);
    for (const r of ready) this.play(r.index, r.item);
  }

  private play(sentence: number, buffer: AudioBuffer): void {
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    this.heard.push(buffer);
    source.connect(this.gain);
    // Sources are scheduled back to back, so these fire in index order and the
    // newest is always the furthest the learner has actually heard.
    source.onended = () => {
      this.scheduled.delete(source);
      this.noteEnded(sentence);
      this.settleSilence();
    };

    const startAt = Math.max(this.ctx.currentTime, this.playAt);
    source.start(startAt);
    this.scheduled.add(source);
    this.playAt = startAt + buffer.duration;
  }

  stop(): void {
    this.stopReplay();
    void this.ctx.suspend();
  }
}
